<template>
  <div class="cobranza-view">
    <CobranzaTopbar
      ref="topbarRef"
      :park="park"
      :loading="loading"
      :alerts="alerts"
      v-model="selectedPeriod"
      v-model:accumulated="accumulated"
      v-model:active-tab="activeTab"
      v-model:search="searchText"
      @new-charge="onNewCharge"
      @generate-statements="onGenerateStatements"
      @open-notifications="onOpenNotifications"
    />

        <div v-if="loading || !metrics" class="cobranza-loading">
        <span class="mdi mdi-loading cobranza-loading__spinner"></span>
        <span class="cobranza-loading__text">Cargando cobranza...</span>
        </div>
        <div v-else class="cobranza-view__body">
  <div class="cobranza-view__main">
    <CobranzaMetricsRow
            :park-id="park.id"
            :metrics="metrics"
            :period="selectedPeriod"
            :accumulated="accumulated"
            @filter-al-corriente="activeTab = 'alCorriente'"
            @filter-vencidos="activeTab = 'vencidos'"
            @filter-vencidos-by-age="onFilterVencidosByAge"
        />
    <FormerContractDebts v-if="park" :park-id="park.id" :reload-key="formerDebtsKey" />
    <CobranzaAccountsList
        v-if="selectedPeriod && activeTab !== 'vencidos'"
        ref="listRef"
        :park-id="park.id"
        :period="selectedPeriod"
        :accumulated="accumulated"
        :active-tab="activeTab"
        @open-account="onOpenAccount"
        @generate-statement="statementContractId = $event"
        @tickets-updated="allAccountsForNav = $event"
        />
        <CobranzaOverdueBoard
        v-else-if="selectedPeriod && activeTab === 'vencidos'"
        ref="boardRef"
        :compact="!!selectedContractId || showNewChargePanel"
        :park-id="park.id"
        :period="selectedPeriod"
        :accumulated="accumulated"
        @open-account="onOpenAccount"
        />
  </div>

  <Transition name="panel-slide">
    <CobranzaAccountPanel
        v-if="selectedContractId && park"
        :park-id="park.id"
        :contract-id="selectedContractId"
        :period="selectedPeriod"
        :account-list="allAccountsForNav"
        @close="selectedContractId = null"
        @navigate="selectedContractId = $event"
        @refresh="onAccountRefresh"
        @open-ticket-hint="onOpenTicketHint"
        />
    <NewManualChargePanel
    v-else-if="showNewChargePanel && park"
    :park-id="park.id"
    :period="selectedPeriod"
    :preselected-contract-id="newChargePreselectedContract"
    @close="showNewChargePanel = false"
    @created="onChargeCreated"
  />
  </Transition>
    <StatementGeneratorPanel
    v-if="statementContractId && park"
    :park-id="park.id"
    :contract-id="statementContractId"
    :period="selectedPeriod"
    @close="statementContractId = null"
    @generated="statementContractId = null"
  />
    <BulkStatementPanel
    v-if="showBulkStatementPanel"
    :park-id="park.id"
    :period="selectedPeriod"
    :rows="bulkStatementRows"
    @close="showBulkStatementPanel = false"
    @done="showBulkStatementPanel = false"
  />
</div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue'
import CobranzaTopbar from '@/components/Cobranza/CobranzaTopbar.vue'
import StatementGeneratorPanel from '@/components/Cobranza/StatementGeneratorPanel.vue'
import { getParks, getParkById } from '@/services/parks'
import { getParkAlerts } from '@/services/alerts'
import CobranzaMetricsRow from '@/components/Cobranza/CobranzaMetricsRow.vue'
import { getCobranzaMetrics } from '@/services/charges'
import CobranzaAccountsList from '@/components/Cobranza/CobranzaAccountsList.vue'
import CobranzaAccountPanel from '@/components/Cobranza/CobranzaAccountPanel.vue'
import NewManualChargePanel from '@/components/Cobranza/NewManualChargePanel.vue'
import BulkStatementPanel from '@/components/Cobranza/BulkStatementPanel.vue'
import CobranzaOverdueBoard from '@/components/Cobranza/CobranzaOverdueBoard.vue'
import FormerContractDebts from '@/components/Cobranza/FormerContractDebts.vue'

const showBulkStatementPanel = ref(false)
const bulkStatementRows = ref([])

function onGenerateStatements() {
  const ctx = (activeTab.value === 'vencidos' ? boardRef.value : listRef.value)?.getGenerationContext()
  if (!ctx) return
  bulkStatementRows.value = ctx.selectedRows.length > 0 ? ctx.selectedRows : ctx.allFilteredRows
  showBulkStatementPanel.value = true
}

const showNewChargePanel = ref(false)
const newChargePreselectedContract = ref(null)

function onNewCharge() {
  newChargePreselectedContract.value = selectedContractId.value
  selectedContractId.value = null
  showNewChargePanel.value = true
}

async function onChargeCreated() {
  showNewChargePanel.value = false
  await (activeTab.value === 'vencidos' ? boardRef : listRef).value?.reload()
  await loadMetrics()
}

// Contrato para el que se abrió «Generar estado de cuenta» desde el menú de tres puntos de la lista.
const statementContractId = ref(null)
const selectedContractId = ref(null)
const allAccountsForNav = ref([])

function onOpenAccount(contractId) {
  selectedContractId.value = contractId
}

const listRef = ref(null)
const boardRef = ref(null)
const formerDebtsKey = ref(0)

// Tras registrar o eliminar un pago en el panel lateral: la lista (o el tablero de vencidos) también se actualiza.
async function onAccountRefresh() {
  await (activeTab.value === 'vencidos' ? boardRef : listRef).value?.reload()
  await loadMetrics()
}

async function loadMetrics() {
  if (!park.value || !selectedPeriod.value) return
  metrics.value = await getCobranzaMetrics(park.value.id, selectedPeriod.value, accumulated.value)
  topbarRef.value?.reloadCounts()
}

function onFilterVencidosByAge() {
  activeTab.value = 'vencidos'
  // TODO: el ordenamiento por antigüedad de deuda depende de la lista principal, que aún no existe
}

function onOpenTicketHint(ticketCode) {
  // TODO: navegar a Mantenimiento y abrir ese ticket específico — por ahora solo referencia el código
  console.log('Ver ticket en Mantenimiento:', ticketCode)
}

const park = ref(null)
const loading = ref(true)
const alerts = ref([])
const selectedPeriod = ref('')
const accumulated = ref(false)
const activeTab = ref('pendientes')
const searchText = ref('')
const topbarRef = ref(null)
const metrics = ref(null)

onMounted(async () => {
  try {
    const parks = await getParks()
    if (parks[0]) {
      const [fullPark, alertsData] = await Promise.all([getParkById(parks[0].id), getParkAlerts(parks[0].id)])
      park.value = fullPark
      alerts.value = alertsData
    }
  } finally {
    loading.value = false
  }
})

watch([selectedPeriod, accumulated], loadMetrics)

function onOpenNotifications() {
  console.log('Abrir notificaciones')
}
</script>

<style scoped>
.cobranza-view { display: flex; flex-direction: column; flex: 1; min-height: 0; }
.cobranza-loading { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 0.75rem; color: var(--text-secondary); }
.cobranza-loading__spinner { font-size: 1.8rem; animation: cobranza-spin 0.8s linear infinite; }
.cobranza-loading__text { font-size: 0.85rem; font-family: var(--font-body); }
@keyframes cobranza-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
.cobranza-view__body { display: flex; flex: 1; min-height: 0; overflow: hidden; }
.cobranza-view__main { flex: 1; min-width: 0; min-height: 0; display: flex; flex-direction: column; overflow: hidden; }
.panel-slide-enter-active, .panel-slide-leave-active { transition: transform 250ms ease-out, opacity 250ms ease-out; }
.panel-slide-enter-from, .panel-slide-leave-to { transform: translateX(24px); opacity: 0; }
</style>